import { useEffect, useMemo, useRef, useState } from "react";
import { Storage } from "aws-amplify";
import { FileRejection } from "react-dropzone";
import {
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiDeleteBin5Line,
  RiFolder3Fill,
  RiAlertLine,
  RiLoader4Line,
  RiArrowRightSLine,
  RiArrowLeftSLine,
  RiCheckDoubleLine,
  RiCloseCircleLine,
} from "react-icons/ri";
import PhotoDropZone from "./PhotoDropZone";
import { matCodeFromPath, FileWithPath } from "./groupByMatCode";
import { humanFileSize } from "./format";
import { mergeUploadedMatcodes } from "./accountFiles";
import { Channel } from "../../constants/enum";
import {
  getProductByMaterialCode,
  markPackageCaseUpdated,
} from "../../apis/product";

// Validation state of a folder's matcode against ProductLibProduct.
type MatcodeStatus = "checking" | "valid" | "invalid";
interface MatcodeInfo {
  status: MatcodeStatus;
  name?: string; // product name (when valid)
  brand?: string; // brand code (when valid), e.g. "FL"
}

// Brand code -> logo (mirrors ProductKnowledge). Used in place of the folder
// icon for validated folders.
const brandLogos: Record<string, string> = {
  FL: "/brands/fineline.png",
  DN: "/brands/deenee.png",
  BE: "/brands/benice.png",
  TS: "/brands/tros.png",
  SM: "/brands/smart.png",
  ES: "/brands/eversense.png",
  TM: "/brands/tomi.png",
  VV: "/brands/vivite.png",
  LT: "/brands/lovlitails.png",
};

type UploadStatus = "pending" | "uploading" | "success" | "failed";

interface UploadItem {
  id: string; // react-dropzone path (unique per file)
  file: FileWithPath;
  matCode: string;
  key: string; // original/<matCode>/<fileName>
  previewUrl: string;
  status: UploadStatus;
  progress: number; // 0..100
  error?: string;
}

interface Group {
  matCode: string;
  items: UploadItem[];
}

// Maps react-dropzone / custom rejection error codes to Thai messages.
function rejectionReasonTh(errors: { code: string; message: string }[]) {
  return errors
    .map((e) => {
      switch (e.code) {
        case "file-too-large":
          return "ไฟล์มีขนาดใหญ่เกิน 20 MB";
        case "file-invalid-type":
          return "ชนิดไฟล์ไม่รองรับ (รับเฉพาะ .jpg .jpeg .png)";
        case "name-not-in-english":
          return "ชื่อไฟล์ต้องเป็นภาษาอังกฤษเท่านั้น";
        default:
          return e.message;
      }
    })
    .join(", ");
}

function UploadTab({
  onBusyChange,
  withAccount = true,
  stickyActionBar = true,
  onUploaded,
}: {
  onBusyChange?: (busy: boolean) => void;
  // When false: no account/channel selector, no S3 metadata, and no
  // UPLOADED_MATCODE.xlsx recording (used by the Product tab's batch upload).
  withAccount?: boolean;
  // When false: the action bar is pinned inside its scroll container (sticky)
  // instead of fixed to the viewport — for use inside a modal.
  stickyActionBar?: boolean;
  // Fired once after a successful batch upload (matcodes that got new photos).
  onUploaded?: (matcodes: string[]) => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [uploadDone, setUploadDone] = useState(false); // step-3: upload finished
  const [validateTab, setValidateTab] = useState<"invalid" | "valid">("valid");
  const [items, setItems] = useState<UploadItem[]>([]);
  const [looseFiles, setLooseFiles] = useState<FileWithPath[]>([]);
  const [rejections, setRejections] = useState<FileRejection[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // Channel written as S3 user-defined metadata (x-amz-meta-channel) on every
  // uploaded object. Selectable so uploads can be classified per channel.
  const [channel, setChannel] = useState<Channel>(Channel.BIGC);
  // matcode -> validation result; a ref tracks which codes were already looked up.
  const [matcodeInfo, setMatcodeInfo] = useState<Record<string, MatcodeInfo>>({});
  const matcodeReq = useRef<Set<string>>(new Set());

  // Let the tab shell lock tab-switching while an upload batch is running.
  useEffect(() => {
    onBusyChange?.(isUploading);
  }, [isUploading, onBusyChange]);

  // Revoke all object URLs on unmount (ref keeps the latest items in scope).
  const itemsRef = useRef<UploadItem[]>(items);
  itemsRef.current = items;
  useEffect(
    () => () => {
      itemsRef.current.forEach((i) => URL.revokeObjectURL(i.previewUrl));
    },
    []
  );

  const handleFilesAdded = (
    accepted: FileWithPath[],
    newRejections: FileRejection[]
  ) => {
    setItems((prev) => {
      const existingIds = new Set(prev.map((i) => i.id));
      const newLoose: FileWithPath[] = [];
      const additions: UploadItem[] = [];

      for (const file of accepted) {
        const id = (file.path ?? file.name).replace(/^\/+/, "");
        const matCode = matCodeFromPath(file);
        if (matCode === null) {
          newLoose.push(file);
          continue;
        }
        if (existingIds.has(id)) continue; // skip duplicate drops
        existingIds.add(id);
        additions.push({
          id,
          file,
          matCode,
          key: `original/${matCode}/${file.name}`,
          previewUrl: URL.createObjectURL(file),
          status: "pending",
          progress: 0,
        });
      }

      if (newLoose.length) setLooseFiles((p) => [...p, ...newLoose]);
      return additions.length ? [...prev, ...additions] : prev;
    });

    if (newRejections.length) {
      setRejections((prev) => [...prev, ...newRejections]);
    }
  };

  const groups: Group[] = useMemo(() => {
    const map = new Map<string, UploadItem[]>();
    for (const item of items) {
      const bucket = map.get(item.matCode);
      if (bucket) bucket.push(item);
      else map.set(item.matCode, [item]);
    }
    return Array.from(map.entries()).map(([matCode, groupItems]) => ({
      matCode,
      items: groupItems,
    }));
  }, [items]);

  // Validate each new matcode against ProductLibProduct (exists? + its name).
  useEffect(() => {
    let cancelled = false;
    const codes = Array.from(new Set(items.map((i) => i.matCode)));
    codes.forEach(async (code) => {
      if (matcodeReq.current.has(code)) return;
      matcodeReq.current.add(code);
      setMatcodeInfo((prev) => ({ ...prev, [code]: { status: "checking" } }));
      try {
        const product = await getProductByMaterialCode(code);
        if (cancelled) return;
        setMatcodeInfo((prev) => ({
          ...prev,
          [code]: product
            ? { status: "valid", name: product.name ?? "", brand: product.brand }
            : { status: "invalid" },
        }));
      } catch {
        if (!cancelled)
          setMatcodeInfo((prev) => ({ ...prev, [code]: { status: "invalid" } }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [items]);

  const summary = useMemo(() => {
    let success = 0;
    let failed = 0;
    for (const item of items) {
      if (item.status === "success") success++;
      else if (item.status === "failed") failed++;
    }
    return { total: items.length, success, failed };
  }, [items]);

  const patchItem = (id: string, patch: Partial<UploadItem>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };

  const uploadItem = async (item: UploadItem): Promise<boolean> => {
    patchItem(item.id, { status: "uploading", progress: 0, error: undefined });
    try {
      await Storage.put(item.key, item.file, {
        level: "public",
        contentType: item.file.type,
        ...(withAccount
          ? { metadata: { channel, matcode: item.matCode } }
          : {}),
        progressCallback: (p: { loaded: number; total: number }) => {
          const pct = p.total ? Math.round((p.loaded / p.total) * 100) : 0;
          patchItem(item.id, { progress: pct });
        },
      });
      patchItem(item.id, { status: "success", progress: 100 });
      return true;
    } catch (err: any) {
      patchItem(item.id, {
        status: "failed",
        error: err?.message ?? "อัปโหลดไม่สำเร็จ",
      });
      return false;
    }
  };

  // Merges the matcodes we just uploaded into the account's UPLOADED_MATCODE.xlsx
  // (dedupe, not append). Failure here must not break the upload flow.
  const recordUploaded = async (matcodes: string[]) => {
    if (!withAccount) return; // no per-account file when account is disabled
    if (matcodes.length === 0) return;
    try {
      await mergeUploadedMatcodes(channel, matcodes);
    } catch (err) {
      console.log("record uploaded matcodes error:", err);
    }
  };

  // Group validity buckets. Invalid ones are surfaced first so they are easy to
  // remove; the whole batch is blocked while any invalid/unchecked code remains.
  const invalidGroups = groups.filter(
    (g) => matcodeInfo[g.matCode]?.status === "invalid"
  );
  const checkingGroups = groups.filter(
    (g) => (matcodeInfo[g.matCode]?.status ?? "checking") === "checking"
  );
  const validGroups = groups.filter(
    (g) => matcodeInfo[g.matCode]?.status === "valid"
  );
  const hasInvalidGroup = invalidGroups.length > 0;
  const isCheckingAny = checkingGroups.length > 0;

  // Post-upload result buckets (step 3): folders with any failed file vs all-success.
  const failedGroups = groups.filter((g) =>
    g.items.some((i) => i.status === "failed")
  );
  const successGroups = groups.filter((g) =>
    g.items.every((i) => i.status === "success")
  );

  const handleUpload = async () => {
    if (hasInvalidGroup || isCheckingAny) return; // blocked by invalid matcode(s)
    setIsUploading(true);
    const toUpload = items.filter((i) => i.status !== "success");
    const uploaded = new Set<string>();
    for (const item of toUpload) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await uploadItem(item);
      if (ok) uploaded.add(item.matCode);
    }
    await recordUploaded(Array.from(uploaded));
    // Stamp packageCaseUpdatedAt on every matcode that got new photos.
    await Promise.all(Array.from(uploaded).map(markPackageCaseUpdated));
    if (uploaded.size > 0) onUploaded?.(Array.from(uploaded));
    setIsUploading(false);
    setUploadDone(true); // switch step-3 to its result view
  };

  const handleRetry = async (id: string) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    setIsUploading(true);
    const ok = await uploadItem(item);
    if (ok) await recordUploaded([item.matCode]);
    setIsUploading(false);
  };

  const removeItem = (id: string) => {
    setItems((prev) => {
      const target = prev.find((i) => i.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((i) => i.id !== id);
    });
  };

  // Remove every file belonging to the given matcodes (one folder or a section).
  const removeMatcodes = (codes: string[]) => {
    const set = new Set(codes);
    setItems((prev) => {
      prev
        .filter((i) => set.has(i.matCode))
        .forEach((i) => URL.revokeObjectURL(i.previewUrl));
      return prev.filter((i) => !set.has(i.matCode));
    });
  };

  const clearAll = () => {
    items.forEach((i) => URL.revokeObjectURL(i.previewUrl));
    setItems([]);
    setLooseFiles([]);
    setRejections([]);
  };

  // Start a fresh batch from step 1 (also drops cached matcode validations).
  const resetAll = () => {
    clearAll();
    matcodeReq.current.clear();
    setMatcodeInfo({});
    setUploadDone(false);
    setStep(1);
  };

  const pendingCount = items.filter((i) => i.status !== "success").length;
  const overallProgress = summary.total
    ? Math.round((summary.success / summary.total) * 100)
    : 0;

  // Fall back to step 1 once every folder has been removed (unless already done).
  useEffect(() => {
    if (step !== 1 && !uploadDone && items.length === 0) setStep(1);
  }, [step, uploadDone, items.length]);

  // Once the "not found" tab is emptied, jump to the "found" tab automatically.
  useEffect(() => {
    if (
      validateTab === "invalid" &&
      invalidGroups.length === 0 &&
      validGroups.length > 0
    )
      setValidateTab("valid");
  }, [validateTab, invalidGroups.length, validGroups.length]);

  // ── one folder card. showCount toggles the done/total badge; gridImages
  //    renders the photos as a square grid; deletable shows a remove button
  //    even for valid folders (validate step) ─────────────────────────────────
  const renderGroup = (
    group: Group,
    showCount = true,
    gridImages = false,
    deletable = false
  ) => {
    const done = group.items.filter((i) => i.status === "success").length;
    const info = matcodeInfo[group.matCode];
    const status = info?.status ?? "checking";
    const name = info?.name;
    const brandLogo =
      status === "valid" && info?.brand ? brandLogos[info.brand] : undefined;
    return (
      <div
        key={group.matCode}
        className={`overflow-hidden bg-white border shadow-sm rounded-xl ${
          status === "valid"
            ? "border-green-200"
            : status === "invalid"
            ? "border-red-200"
            : "border-gray-200"
        }`}
      >
        <div
          className={`flex items-center justify-between gap-2 px-4 border-b ${
            status === "valid" ? "py-2" : "py-3"
          } ${
            status === "valid"
              ? "border-green-100 bg-green-50"
              : status === "invalid"
              ? "border-red-100 bg-red-50"
              : "border-gray-100 bg-gray-50/70"
          }`}
        >
          <div className="flex items-center min-w-0 gap-2">
            {brandLogo ? (
              <img
                src={brandLogo}
                alt={info?.brand ?? ""}
                title={info?.brand ?? ""}
                className="object-contain w-10 h-10 shrink-0"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src =
                    "/brands/no_image.png";
                }}
              />
            ) : (
              <RiFolder3Fill
                className={`shrink-0 ${
                  status === "valid"
                    ? "text-green-500"
                    : status === "invalid"
                    ? "text-red-400"
                    : "text-amber-400"
                }`}
                size={20}
              />
            )}
            <span className="font-semibold text-gray-800 truncate">
              {group.matCode}
            </span>
            {status === "valid" && name && (
              <span className="text-sm font-normal text-green-700 truncate">
                · {name}
              </span>
            )}
            {status === "checking" && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-400 shrink-0">
                <RiLoader4Line className="animate-spin" size={13} /> ตรวจสอบรหัส…
              </span>
            )}
            {status === "invalid" && (
              <span className="text-xs font-medium text-red-600 shrink-0">
                ไม่พบรหัสนี้ในระบบ
              </span>
            )}
          </div>
          {status === "invalid" || deletable ? (
            <button
              type="button"
              onClick={() => removeMatcodes([group.matCode])}
              disabled={isUploading}
              title="ลบโฟลเดอร์"
              className="inline-flex items-center justify-center rounded-lg border border-red-300 bg-white p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50 shrink-0"
            >
              <RiDeleteBin5Line size={15} />
            </button>
          ) : showCount ? (
            <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-gray-500 shadow-sm shrink-0">
              {done}/{group.items.length}
            </span>
          ) : null}
        </div>
        {gridImages ? (
          <div className="grid grid-cols-4 gap-2 p-3 sm:grid-cols-6">
            {group.items.map((item) => (
              <div
                key={item.id}
                className="relative overflow-hidden border border-gray-200 rounded-lg group aspect-square bg-gray-50"
              >
                <img
                  src={item.previewUrl}
                  alt={item.file.name}
                  className="object-cover w-full h-full"
                />
                {!isUploading && (
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    title="ลบรูปนี้"
                    className="absolute p-1 text-white transition-opacity rounded-full opacity-0 top-1 right-1 bg-black/50 group-hover:opacity-100 hover:bg-red-600"
                  >
                    <RiDeleteBin5Line size={12} />
                  </button>
                )}
                <span className="absolute inset-x-0 bottom-0 px-1 py-0.5 text-[10px] text-white truncate bg-black/40 pointer-events-none">
                  {item.file.name}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <ul className="divide-y divide-gray-50">
            {group.items.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-4 py-2.5">
              <img
                src={item.previewUrl}
                alt={item.file.name}
                className="object-cover w-10 h-10 border border-gray-200 rounded-md bg-gray-50 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-gray-700 truncate">
                    {item.file.name}
                  </span>
                  <span className="text-xs text-gray-400 shrink-0">
                    {humanFileSize(item.file.size)}
                  </span>
                </div>
                {(item.status === "uploading" || item.status === "success") && (
                  <div className="w-full h-1.5 mt-1.5 overflow-hidden bg-gray-100 rounded-full">
                    <div
                      className={`h-full rounded-full transition-all duration-200 ${
                        item.status === "success" ? "bg-green-500" : "bg-blue-500"
                      }`}
                      style={{ width: `${item.progress}%` }}
                    />
                  </div>
                )}
                {item.status === "failed" && item.error && (
                  <p className="mt-0.5 text-xs text-red-500 truncate">
                    {item.error}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-end w-8 shrink-0">
                {item.status === "success" && (
                  <span className="flex items-center justify-center w-6 h-6 text-green-600 rounded-full bg-green-50">
                    <RiCheckLine size={15} />
                  </span>
                )}
                {item.status === "uploading" && (
                  <span className="text-xs font-medium text-blue-500">
                    {item.progress}%
                  </span>
                )}
                {item.status === "failed" && (
                  <button
                    type="button"
                    onClick={() => handleRetry(item.id)}
                    disabled={isUploading}
                    className="flex items-center justify-center w-6 h-6 text-red-500 rounded-full bg-red-50 hover:bg-red-100 disabled:opacity-40"
                    title="ลองใหม่"
                  >
                    <RiRefreshLine size={14} />
                  </button>
                )}
                {item.status === "pending" && !isUploading && (
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="flex items-center justify-center w-6 h-6 text-gray-400 rounded-full hover:bg-gray-100 hover:text-red-500"
                    title="ลบ"
                  >
                    <RiDeleteBin5Line size={14} />
                  </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  // ── one step-2 section (title + count + optional delete-all) ─────────────
  const renderSection = (
    title: string,
    sectionGroups: Group[],
    opts: {
      tone: "red" | "green" | "gray";
      deleteAll?: boolean;
      showCount?: boolean;
      gridImages?: boolean;
    }
  ) => {
    if (sectionGroups.length === 0) return null;
    const toneText =
      opts.tone === "red"
        ? "text-red-700"
        : opts.tone === "green"
        ? "text-green-700"
        : "text-gray-600";
    const toneBadge =
      opts.tone === "red"
        ? "bg-red-100 text-red-700"
        : opts.tone === "green"
        ? "bg-green-100 text-green-700"
        : "bg-gray-100 text-gray-600";
    return (
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className={`inline-flex items-center gap-2 text-sm font-semibold ${toneText}`}>
            {title}
            <span
              className={`inline-flex items-center justify-center min-w-[1.25rem] rounded-full px-1.5 text-xs font-bold ${toneBadge}`}
            >
              {sectionGroups.length}
            </span>
          </h3>
          {opts.deleteAll && (
            <button
              type="button"
              onClick={() =>
                removeMatcodes(sectionGroups.map((g) => g.matCode))
              }
              disabled={isUploading}
              className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              <RiDeleteBin5Line size={13} /> ลบทั้งหมด
            </button>
          )}
        </div>
        <div className="space-y-4">
          {sectionGroups.map((g) =>
            renderGroup(g, opts.showCount ?? true, opts.gridImages ?? false)
          )}
        </div>
      </div>
    );
  };

  const StepPill = ({
    n,
    label,
    active,
    done,
  }: {
    n: number;
    label: string;
    active: boolean;
    done?: boolean;
  }) => (
    <div className="flex items-center gap-2">
      <span
        className={`flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold ${
          active
            ? "bg-blue-600 text-white"
            : done
            ? "bg-green-500 text-white"
            : "bg-gray-200 text-gray-500"
        }`}
      >
        {done ? <RiCheckLine size={16} /> : n}
      </span>
      <span
        className={`text-sm font-medium ${
          active ? "text-gray-800" : "text-gray-400"
        }`}
      >
        {label}
      </span>
    </div>
  );

  return (
    <>
      {/* Stepper header */}
      <div className="flex items-center justify-center gap-3 mb-4">
        <StepPill n={1} label="เลือกโฟลเดอร์" active={step === 1} done={step > 1} />
        <div className="w-8 h-px bg-gray-300" />
        <StepPill n={2} label="ตรวจสอบ" active={step === 2} done={step > 2} />
        <div className="w-8 h-px bg-gray-300" />
        <StepPill n={3} label="อัปโหลด" active={step === 3} done={uploadDone} />
      </div>

      {/* ── STEP 1 : upload ─────────────────────────────────────────────── */}
      {step === 1 && (
        <>
          {withAccount && (
            <div className="flex items-center justify-center gap-2 mb-4">
              <label className="text-sm font-medium text-gray-600">
                Account(s)
              </label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value as Channel)}
                disabled={isUploading}
                className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm focus:border-blue-400 focus:outline-none disabled:opacity-50"
              >
                {Object.values(Channel).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-400">
                ใช้เป็น metadata ของไฟล์ที่อัปโหลด
              </span>
            </div>
          )}

          <PhotoDropZone onFilesAdded={handleFilesAdded} disabled={isUploading} />

          {items.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                <RiFolder3Fill className="text-gray-400" size={14} />
                {groups.length} โฟลเดอร์
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                {summary.total} ไฟล์
              </span>
            </div>
          )}

          {(looseFiles.length > 0 || rejections.length > 0) && (
            <div className="p-4 mt-4 border rounded-xl border-amber-200 bg-amber-50">
              <p className="flex items-center gap-1.5 mb-2 text-sm font-semibold text-amber-800">
                <RiAlertLine size={16} />
                ข้าม {looseFiles.length + rejections.length} ไฟล์
              </p>
              <ul className="space-y-1 text-xs text-amber-700">
                {looseFiles.map((file, index) => (
                  <li key={`loose_${file.name}_${index}`} className="truncate">
                    <span className="font-medium">{file.name}</span> —
                    ไม่ได้อยู่ในโฟลเดอร์ (ไม่มีรหัสโฟลเดอร์)
                  </li>
                ))}
                {rejections.map((rejection, index) => (
                  <li
                    key={`reject_${rejection.file.name}_${index}`}
                    className="truncate"
                  >
                    <span className="font-medium">{rejection.file.name}</span> —{" "}
                    {rejectionReasonTh(rejection.errors)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            {items.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                className="px-4 py-2 text-sm font-medium text-gray-600 transition-colors border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                ล้าง
              </button>
            )}
            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={items.length === 0}
              className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
            >
              ถัดไป: ตรวจสอบ & ยืนยัน
              <RiArrowRightSLine size={18} />
            </button>
          </div>
        </>
      )}

      {/* ── STEP 2 : validate & submit ──────────────────────────────────── */}
      {step === 2 && (
        <>
          {/* Tabs: found (left) / not found (right) — full width, split evenly */}
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => setValidateTab("valid")}
              className={`-mb-px flex flex-1 items-center justify-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium text-green-600 ${
                validateTab === "valid"
                  ? "border-green-500"
                  : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <RiCheckDoubleLine size={16} />
              พบในระบบ
              <span className="rounded-full bg-green-100 px-1.5 text-xs font-bold text-green-700">
                {validGroups.length}
              </span>
              <span className="text-xs font-normal text-green-600/70">
                MatCode(s)
              </span>
            </button>
            <button
              type="button"
              onClick={() => setValidateTab("invalid")}
              className={`-mb-px flex flex-1 items-center justify-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium text-red-600 ${
                validateTab === "invalid"
                  ? "border-red-500"
                  : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <RiCloseCircleLine size={16} />
              ไม่พบในระบบ
              <span className="rounded-full bg-red-100 px-1.5 text-xs font-bold text-red-700">
                {invalidGroups.length}
              </span>
              <span className="text-xs font-normal text-red-600/70">
                MatCode(s)
              </span>
            </button>
          </div>

          {isCheckingAny && (
            <p className="flex items-center gap-1.5 mt-3 text-xs text-gray-400">
              <RiLoader4Line className="animate-spin" size={13} />{" "}
              กำลังตรวจสอบอีก {checkingGroups.length} โฟลเดอร์…
            </p>
          )}

          {/* Tab content */}
          <div className="mt-4">
            {validateTab === "invalid" ? (
              invalidGroups.length === 0 ? (
                <div className="py-10 text-sm text-center text-gray-400">
                  ไม่มีโฟลเดอร์ที่ไม่พบในระบบ 🎉
                </div>
              ) : (
                <>
                  <div className="flex justify-end mb-2">
                    <button
                      type="button"
                      onClick={() =>
                        removeMatcodes(invalidGroups.map((g) => g.matCode))
                      }
                      disabled={isUploading}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      <RiDeleteBin5Line size={13} /> ลบทั้งหมด
                    </button>
                  </div>
                  <div className="space-y-4">
                    {invalidGroups.map((g) => renderGroup(g, false, true, true))}
                  </div>
                </>
              )
            ) : validGroups.length === 0 ? (
              <div className="py-10 text-sm text-center text-gray-400">
                ยังไม่มีโฟลเดอร์ที่พบในระบบ
              </div>
            ) : (
              <>
                <div className="flex justify-end mb-2">
                  <button
                    type="button"
                    onClick={() =>
                      removeMatcodes(validGroups.map((g) => g.matCode))
                    }
                    disabled={isUploading}
                    className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <RiDeleteBin5Line size={13} /> ลบทั้งหมด
                  </button>
                </div>
                <div className="space-y-4">
                  {validGroups.map((g) => renderGroup(g, false, true, true))}
                </div>
              </>
            )}
          </div>

          {/* Action bar — validate only; upload happens in step 3 */}
          <div
            className={`z-40 mt-6 border-t border-gray-200 bg-white/90 backdrop-blur ${
              stickyActionBar ? "fixed inset-x-0 bottom-0" : "sticky bottom-0"
            }`}
          >
            <div className="flex items-center justify-between max-w-4xl gap-4 px-4 py-3 mx-auto">
              <span
                className={`text-sm truncate ${
                  hasInvalidGroup ? "text-red-600 font-medium" : "text-gray-600"
                }`}
              >
                {hasInvalidGroup
                  ? "มีโฟลเดอร์รหัสไม่ถูกต้อง — ลบออกก่อนจึงไปต่อได้"
                  : isCheckingAny
                  ? "กำลังตรวจสอบรหัสสินค้า…"
                  : `พร้อมไปต่อ — พบในระบบ ${validGroups.length} โฟลเดอร์`}
              </span>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-600 transition-colors border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  <RiArrowLeftSLine size={18} />
                  ย้อนกลับ
                </button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  disabled={
                    hasInvalidGroup ||
                    isCheckingAny ||
                    validGroups.length === 0
                  }
                  title={
                    hasInvalidGroup
                      ? "มีโฟลเดอร์รหัสไม่ถูกต้อง"
                      : isCheckingAny
                      ? "กำลังตรวจสอบรหัส"
                      : undefined
                  }
                  className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                >
                  ถัดไป: อัปโหลด
                  <RiArrowRightSLine size={18} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── STEP 3 : upload + result summary ────────────────────────────── */}
      {step === 3 && !uploadDone && (
        <>
          <div className="flex flex-col items-center mb-1 text-center">
            <h3 className="text-lg font-semibold text-gray-800">พร้อมอัปโหลด</h3>
            <p className="mt-0.5 text-sm text-gray-500">
              ตรวจรายการอีกครั้งแล้วกดอัปโหลด
            </p>
          </div>

          {/* Summary counts */}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
              <RiFolder3Fill size={14} className="text-green-500" />{" "}
              {validGroups.length} โฟลเดอร์
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
              {pendingCount} ไฟล์
            </span>
          </div>

          {renderSection("รายการที่จะอัปโหลด", validGroups, { tone: "green" })}

          {/* Action bar */}
          <div
            className={`z-40 mt-6 border-t border-gray-200 bg-white/90 backdrop-blur ${
              stickyActionBar ? "fixed inset-x-0 bottom-0" : "sticky bottom-0"
            }`}
          >
            <div className="flex items-center justify-between max-w-4xl gap-4 px-4 py-3 mx-auto">
              <div className="flex items-center flex-1 min-w-0 gap-3">
                <div className="hidden w-32 h-2 overflow-hidden bg-gray-100 rounded-full sm:block">
                  <div
                    className="h-full transition-all duration-300 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600 truncate">
                  {isUploading
                    ? "กำลังอัปโหลด…"
                    : `พร้อมอัปโหลด ${pendingCount} ไฟล์`}
                </span>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  disabled={isUploading}
                  className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-600 transition-colors border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                >
                  <RiArrowLeftSLine size={18} />
                  ย้อนกลับ
                </button>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={isUploading || pendingCount === 0}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
                >
                  {isUploading ? "กำลังอัปโหลด…" : `อัปโหลด ${pendingCount} ไฟล์`}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── STEP 3 : result summary (after upload) ──────────────────────── */}
      {step === 3 && uploadDone && (
        <>
          <div className="flex flex-col items-center mb-1 text-center">
            {summary.failed > 0 ? (
              <>
                <span className="flex items-center justify-center w-12 h-12 mb-2 text-amber-600 rounded-full bg-amber-100">
                  <RiAlertLine size={26} />
                </span>
                <h3 className="text-lg font-semibold text-gray-800">
                  อัปโหลดเสร็จสิ้น (มีบางส่วนล้มเหลว)
                </h3>
              </>
            ) : (
              <>
                <span className="flex items-center justify-center w-12 h-12 mb-2 text-green-600 rounded-full bg-green-100">
                  <RiCheckLine size={26} />
                </span>
                <h3 className="text-lg font-semibold text-gray-800">
                  อัปโหลดเสร็จสิ้น
                </h3>
              </>
            )}
          </div>

          {/* Summary counts */}
          <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
              <RiCheckLine size={14} /> สำเร็จ {summary.success} ไฟล์
            </span>
            {summary.failed > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700">
                <RiErrorWarningLine size={14} /> ล้มเหลว {summary.failed} ไฟล์
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
              <RiFolder3Fill size={14} className="text-gray-400" />{" "}
              {groups.length} โฟลเดอร์
            </span>
          </div>

          {/* failed first (can retry per file), then success */}
          {renderSection("ล้มเหลว", failedGroups, { tone: "red" })}
          {renderSection("สำเร็จ", successGroups, { tone: "green" })}

          {/* Action bar */}
          <div
            className={`z-40 mt-6 border-t border-gray-200 bg-white/90 backdrop-blur ${
              stickyActionBar ? "fixed inset-x-0 bottom-0" : "sticky bottom-0"
            }`}
          >
            <div className="flex items-center justify-between max-w-4xl gap-4 px-4 py-3 mx-auto">
              <span className="text-sm text-gray-600 truncate">
                อัปโหลดสำเร็จ {summary.success}/{summary.total} ไฟล์
              </span>
              <button
                type="button"
                onClick={resetAll}
                disabled={isUploading}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md disabled:opacity-50"
              >
                อัปโหลดชุดใหม่
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default UploadTab;
